import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, ChevronRight } from "lucide-react";
import { api, imageSourceFor } from "@/lib/api";
import { PrimaryButton, SectionLabel } from "@/components/mo";

export default function Home() {
  const navigate = useNavigate();
  const { data: studio } = useQuery({ queryKey: ["studio"], queryFn: api.getStudio });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: api.getStyles });

  const heroImg = imageSourceFor({ imagePath: studio?.heroImagePath, imageUrl: studio?.heroImageUrl });
  const [heroLoaded, setHeroLoaded] = React.useState(false);

  return (
    <div className="mo-rise">
      {/* Hero */}
      <section className="relative h-[460px] overflow-hidden bg-mo-surface md:rounded-b-3xl">
        {heroImg ? (
          <img
            src={heroImg}
            alt=""
            data-testid="home-hero-image"
            onLoad={() => setHeroLoaded(true)}
            className={"absolute inset-0 h-full w-full object-cover transition-opacity duration-700 " + (heroLoaded ? "opacity-100" : "opacity-0")}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-b from-mo-surface/40 via-mo-surface/30 to-mo-surface" />
        <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-3 p-6 md:p-10">
          <h1 className="max-w-xl font-display text-4xl font-bold leading-[1.05] text-mo-ink sm:text-5xl lg:text-6xl">
            {(studio?.heroTitle || "PIÙ DI UN TATUAGGIO, UN'ESPERIENZA.")}
          </h1>
          <p className="max-w-md font-body text-sm text-mo-muted">
            {studio?.heroSubtitle || "Arte, persone, storie. Benvenuto nel mondo Mörphösy."}
          </p>
        </div>
      </section>

      <div className="flex flex-col gap-6 px-5 py-6 md:px-10">
        <PrimaryButton data-testid="home-prenota-button" icon={Calendar} onClick={() => navigate("/prenota")}>
          Prenota ora
        </PrimaryButton>

        <div className="mt-2 flex items-center justify-between">
          <SectionLabel>I Nostri Stili</SectionLabel>
          <button
            data-testid="home-see-styles"
            onClick={() => navigate("/stili")}
            className="font-body text-xs font-bold text-mo-brand hover:underline"
          >
            Vedi tutti
          </button>
        </div>

        <div className="no-scrollbar -mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
          {styles.map((s) => {
            const src = imageSourceFor(s);
            return (
              <button
                key={s.id}
                data-testid={`home-style-${s.id}`}
                onClick={() => navigate("/prenota")}
                className="relative h-48 w-36 flex-shrink-0 overflow-hidden rounded-xl border border-mo-line bg-mo-surface2 text-left"
              >
                {src ? (
                  <img src={src} alt={s.name} className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-mo-surface3" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                <span className="absolute bottom-3 left-3 font-body text-[13px] font-extrabold uppercase tracking-wider text-white">
                  {s.name}
                </span>
              </button>
            );
          })}
        </div>

        <button
          data-testid="home-guide"
          onClick={() => navigate("/prenota")}
          className="flex items-center gap-4 rounded-2xl border border-mo-line bg-mo-card p-6 text-left transition-all hover:-translate-y-0.5 hover:border-mo-line2"
        >
          <div className="flex-1">
            <h3 className="font-display text-2xl font-bold text-mo-ink">Il tuo progetto, la nostra guida</h3>
            <p className="mt-1 font-body text-[13px] text-mo-muted">
              Dall'idea alla cura finale, siamo con te in ogni fase.
            </p>
          </div>
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-mo-brand">
            <ChevronRight className="h-5 w-5 text-mo-onbrand" />
          </span>
        </button>
      </div>
    </div>
  );
}
